import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { UserPlus, Users, DollarSign, TrendingUp, Search, Settings, CheckCircle2, XCircle, Banknote, TrendingDown } from 'lucide-react';

export const AdminAgentsPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [commissionsDialog, setCommissionsDialog] = useState(false);
  const [fundDialog, setFundDialog] = useState(false);
  const [recoverDialog, setRecoverDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [newAgent, setNewAgent] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    business_name: '',
    province: '',
    city: '',
    area: '',
    deposit_commission: 2.0,
    withdrawal_commission: 2.0,
    float_cap: 50000
  });

  // Fund form
  const [fundAmount, setFundAmount] = useState('');
  const [fundSource, setFundSource] = useState('treasury');
  const [fundReference, setFundReference] = useState('');
  const [fundNotes, setFundNotes] = useState('');

  // Recover form
  const [recoverAmount, setRecoverAmount] = useState('');
  const [recoverReason, setRecoverReason] = useState('');

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/agents?limit=500`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    
    if (!newAgent.email || !newAgent.password || !newAgent.name) {
      toast.error('Email, password, and name are required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/agents/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newAgent)
      });

      if (response.ok) {
        toast.success('Agent created successfully!');
        setCreateDialog(false);
        setNewAgent({
          email: '',
          password: '',
          name: '',
          phone: '',
          business_name: '',
          province: '',
          city: '',
          area: '',
          deposit_commission: 2.0,
          withdrawal_commission: 2.0,
          float_cap: 50000
        });
        fetchAgents();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Failed to create agent');
      }
    } catch (error) {
      toast.error('Failed to create agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCommissions = async () => {
    if (!selectedAgent) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/agents/${selectedAgent.user_id}/commissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          deposit_commission: selectedAgent.deposit_commission,
          withdrawal_commission: selectedAgent.withdrawal_commission
        })
      });

      if (response.ok) {
        toast.success('Commission rates updated!');
        setCommissionsDialog(false);
        fetchAgents();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update commissions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFundAgent = async () => {
    if (!selectedAgent || !fundAmount || parseFloat(fundAmount) <= 0) {
      toast.error('Enter a valid funding amount');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/agents/${selectedAgent.user_id}/fund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(fundAmount),
          source: fundSource,
          reference: fundReference,
          notes: fundNotes
        })
      });

      if (response.ok) {
        toast.success('Agent funded successfully!');
        setFundDialog(false);
        fetchAgents();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Failed to fund agent');
      }
    } catch (error) {
      toast.error('Failed to fund agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecoverFloat = async () => {
    if (!selectedAgent || !recoverAmount || parseFloat(recoverAmount) <= 0) {
      toast.error('Enter a valid recovery amount');
      return;
    }

    if (!recoverReason) {
      toast.error('Reason is required for float recovery');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/agents/${selectedAgent.user_id}/recover-float`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(recoverAmount),
          reason: recoverReason
        })
      });

      if (response.ok) {
        toast.success('Float recovered successfully!');
        setRecoverDialog(false);
        fetchAgents();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Failed to recover float');
      }
    } catch (error) {
      toast.error('Failed to recover float');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAgentStatus = async (agent) => {
    const newStatus = agent.status === 'active' ? 'inactive' : 'active';
    
    try {
      const response = await fetch(`${API_URL}/api/admin/agents/${agent.user_id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchAgents();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredAgents = agents.filter(agent => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      agent.email?.toLowerCase().includes(search) ||
      agent.name?.toLowerCase().includes(search) ||
      agent.business_name?.toLowerCase().includes(search)
    );
  });

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.status === 'active').length,
    totalFloat: agents.reduce((sum, a) => sum + (a.float_balance || 0), 0),
    totalCommission: agents.reduce((sum, a) => sum + (a.commission_balance || 0), 0)
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Agent Management</h1>
            <p className="text-muted-foreground mt-1">Create, monitor, and manage agents</p>
          </div>
          <Button onClick={() => setCreateDialog(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Create New Agent
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">{stats.active} active</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Float</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R {stats.totalFloat.toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Commissions</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R {stats.totalCommission.toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Commission</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                R {stats.total > 0 ? (stats.totalCommission / stats.total).toFixed(2) : '0.00'}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search agents..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Agents ({filteredAgents.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8">Loading...</p>
            ) : filteredAgents.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No agents found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Float</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Rates</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgents.map((agent) => (
                    <TableRow key={agent.user_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-sm text-muted-foreground">{agent.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{agent.business_name || '-'}</TableCell>
                      <TableCell>
                        {agent.city && <p className="text-sm">{agent.city}</p>}
                        {agent.province && <p className="text-xs text-muted-foreground">{agent.province}</p>}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">R {agent.float_balance?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-muted-foreground">Cap: {agent.float_cap || 0}</p>
                      </TableCell>
                      <TableCell className="font-medium text-green-600">
                        R {agent.commission_balance?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <p>Dep: {agent.deposit_commission || 2}%</p>
                          <p>With: {agent.withdrawal_commission || 2}%</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={agent.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                          {agent.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedAgent({...agent});
                              setFundAmount('');
                              setFundSource('treasury');
                              setFundReference('');
                              setFundNotes(`Float funding for ${agent.name}`);
                              setFundDialog(true);
                            }}
                            title="Fund Agent"
                          >
                            <Banknote className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedAgent({...agent});
                              setRecoverAmount('');
                              setRecoverReason('');
                              setRecoverDialog(true);
                            }}
                            title="Recover Float"
                          >
                            <TrendingDown className="h-4 w-4 text-red-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedAgent({...agent});
                              setCommissionsDialog(true);
                            }}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleAgentStatus(agent)}
                          >
                            {agent.status === 'active' ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Agent</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAgent} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email*</Label>
                <Input
                  type="email"
                  value={newAgent.email}
                  onChange={(e) => setNewAgent({...newAgent, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Password*</Label>
                <Input
                  type="password"
                  value={newAgent.password}
                  onChange={(e) => setNewAgent({...newAgent, password: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name*</Label>
                <Input
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({...newAgent, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newAgent.phone}
                  onChange={(e) => setNewAgent({...newAgent, phone: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Business Name</Label>
              <Input
                value={newAgent.business_name}
                onChange={(e) => setNewAgent({...newAgent, business_name: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Province</Label>
                <Input
                  value={newAgent.province}
                  onChange={(e) => setNewAgent({...newAgent, province: e.target.value})}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={newAgent.city}
                  onChange={(e) => setNewAgent({...newAgent, city: e.target.value})}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Commission & Float</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Deposit Com. (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newAgent.deposit_commission}
                    onChange={(e) => setNewAgent({...newAgent, deposit_commission: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Withdrawal Com. (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newAgent.withdrawal_commission}
                    onChange={(e) => setNewAgent({...newAgent, withdrawal_commission: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Float Cap (ZAR)</Label>
                  <Input
                    type="number"
                    value={newAgent.float_cap}
                    onChange={(e) => setNewAgent({...newAgent, float_cap: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Agent'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={commissionsDialog} onOpenChange={setCommissionsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Commission Rates</DialogTitle>
          </DialogHeader>
          {selectedAgent && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Agent: {selectedAgent.name} ({selectedAgent.email})
              </p>
              <div>
                <Label>Deposit Commission (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={selectedAgent.deposit_commission || 2}
                  onChange={(e) => setSelectedAgent({...selectedAgent, deposit_commission: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Withdrawal Commission (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={selectedAgent.withdrawal_commission || 2}
                  onChange={(e) => setSelectedAgent({...selectedAgent, withdrawal_commission: parseFloat(e.target.value)})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommissionsDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateCommissions} disabled={submitting}>
              {submitting ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fund Agent Dialog */}
      <Dialog open={fundDialog} onOpenChange={setFundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fund Agent - {selectedAgent?.name}</DialogTitle>
          </DialogHeader>

          {selectedAgent && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedAgent.name}</p>
                <p className="text-xs text-muted-foreground">{selectedAgent.email}</p>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Current Float</p>
                    <p className="font-medium">R {selectedAgent.float_balance?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Float Cap</p>
                    <p className="font-medium">R {selectedAgent.float_cap?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Amount (ZAR)*</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fundAmount}
                  onChange={(e) => setFundAmount(e.target.value)}
                  placeholder="Enter funding amount"
                />
              </div>

              <div>
                <Label>Funding Source*</Label>
                <select
                  value={fundSource}
                  onChange={(e) => setFundSource(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="treasury">Treasury</option>
                  <option value="admin_wallet">Admin Wallet</option>
                </select>
              </div>

              <div>
                <Label>Reference Number</Label>
                <Input
                  value={fundReference}
                  onChange={(e) => setFundReference(e.target.value)}
                  placeholder="Transaction reference"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={fundNotes}
                  onChange={(e) => setFundNotes(e.target.value)}
                  placeholder="Funding notes..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFundDialog(false)}>Cancel</Button>
            <Button onClick={handleFundAgent} disabled={submitting}>
              {submitting ? 'Funding...' : 'Fund Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recover Float Dialog */}
      <Dialog open={recoverDialog} onOpenChange={setRecoverDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recover Float - {selectedAgent?.name}</DialogTitle>
          </DialogHeader>

          {selectedAgent && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedAgent.name}</p>
                <p className="text-xs text-muted-foreground">{selectedAgent.email}</p>
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground">Current Float</p>
                  <p className="text-lg font-bold">R {selectedAgent.float_balance?.toFixed(2) || '0.00'}</p>
                </div>
              </div>

              <div>
                <Label>Amount to Recover (ZAR)*</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recoverAmount}
                  onChange={(e) => setRecoverAmount(e.target.value)}
                  placeholder="Enter amount to recover"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum: R {selectedAgent.float_balance?.toFixed(2) || '0.00'}
                </p>
              </div>

              <div>
                <Label>Reason for Recovery*</Label>
                <Input
                  value={recoverReason}
                  onChange={(e) => setRecoverReason(e.target.value)}
                  placeholder="Explain why float is being recovered..."
                  required
                />
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ This action will deduct float from the agent's wallet and notify them immediately.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecoverDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRecoverFloat} disabled={submitting}>
              {submitting ? 'Recovering...' : 'Recover Float'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
